import * as React from "react"
import { Loader2, UserPlus } from "lucide-react"

import { Button } from "@/shared/components/ui/button"
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from "@/shared/components/ui/searchable-select"
import { cn } from "@/shared/lib/utils"

interface AssignmentPillProps {
  /** Currently-assigned uuid ("" when unassigned). */
  value: string
  /** Display label for the assigned person (name or email). */
  label?: string | null
  /** Placeholder shown on the trigger when nothing is assigned. */
  placeholder?: string
  /** Grouped, searchable options for the picker. */
  groups: SearchableSelectGroup[]
  searchValue?: string
  onSearchChange?: (value: string) => void
  loading?: boolean
  searchPlaceholder?: string
  emptyMessage?: string
  /** Called with the selected uuid ("" clears the assignment). */
  onAssign: (uuid: string) => void
  /** When true, prepends an "Unassign" row while a value is set. */
  allowClear?: boolean
  clearLabel?: string
  /** Notified when the dropdown opens/closes (e.g. to gate a fetch). */
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  pending?: boolean
  /** Icon shown on the trigger (defaults to UserPlus). */
  icon?: React.ReactNode
  contentClassName?: string
  "data-testid"?: string
}

/**
 * Interactive assignment pill styled exactly like the claims assignee
 * control: green when assigned, neutral "Assign …" when empty, a spinner
 * while the mutation is in flight. Wraps the shared `SearchableSelect` with a
 * custom trigger so it can be reused for any "search a person and assign
 * them" surface (inspector, follow-up, etc.).
 */
export function AssignmentPill({
  value,
  label,
  placeholder = "Assign",
  groups,
  searchValue,
  onSearchChange,
  loading,
  searchPlaceholder = "Search people...",
  emptyMessage = "No members found",
  onAssign,
  allowClear = true,
  clearLabel = "Unassign",
  onOpenChange,
  disabled,
  pending,
  icon,
  contentClassName = "min-w-[240px]",
  "data-testid": testId,
}: AssignmentPillProps) {
  const resolvedGroups = React.useMemo<SearchableSelectGroup[]>(() => {
    if (!allowClear || !value) return groups
    return [
      { label: "", options: [{ value: "", label: clearLabel }] },
      ...groups,
    ]
  }, [allowClear, value, groups, clearLabel])

  const assigned = !!value

  return (
    <SearchableSelect
      value={value}
      onValueChange={onAssign}
      onOpenChange={onOpenChange}
      groups={resolvedGroups}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      disabled={disabled || pending}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      loading={loading}
      contentClassName={contentClassName}
      data-testid={testId}
      renderTrigger={
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || pending}
          className={cn(
            "flex cursor-pointer items-center gap-1 rounded-[6px] px-3 py-1.5 text-[11px] transition-all duration-150",
            assigned
              ? "border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
              : "border-[var(--rule-strong)] bg-[var(--status-info-bg)] text-[var(--ink)] hover:border-[var(--ink-3)]",
          )}
        >
          {pending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            (icon ?? <UserPlus className="h-3 w-3" />)
          )}
          {(assigned ? label : null) ?? placeholder}
        </Button>
      }
    />
  )
}
